
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Search, Crown } from "lucide-react";
import UserTable from "./UserTable";
import { fetchUsers, USERS_PER_PAGE, UserWithSubscription } from "./adminUserUtils";
import { supabase } from "@/integrations/supabase/client";

const AdminUserManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: users, isLoading, error } = useQuery({
    queryKey: ["admin-users", searchTerm, page],
    queryFn: () => fetchUsers(searchTerm, page),
    staleTime: 1000 * 60,
  });

  const deactivateSubscription = useMutation({
    mutationFn: async (subscriptionId: string) => {
      console.log('Deactivating subscription:', subscriptionId);
      const { error } = await supabase
        .from("user_subscriptions")
        .update({
          status: "cancelled",
          end_date: new Date().toISOString(),
        })
        .eq("id", subscriptionId);

      if (error) {
        console.error('Deactivation error:', error);
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User subscription has been deactivated.",
      });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error) => {
      console.error('Deactivate mutation error:', error);
      toast({
        title: "Error",
        description: "Failed to deactivate subscription.",
        variant: "destructive",
      });
    },
  });

  const activateSubscription = useMutation({
    mutationFn: async (userId: string) => {
      console.log('Activating subscription for user:', userId);
      
      const { error: cancelError } = await supabase
        .from("user_subscriptions")
        .update({ status: "cancelled", end_date: new Date().toISOString() })
        .eq("user_id", userId)
        .eq("status", "active");

      if (cancelError) {
        console.warn('Error cancelling existing subscriptions:', cancelError);
      }

      const { error, data } = await supabase
        .from("user_subscriptions")
        .insert({
          user_id: userId,
          plan: "monthly",
          amount: 19.99,
          status: "active",
          start_date: new Date().toISOString(),
          end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        });

      if (error) {
        console.error("Supabase Insert Error (Activate):", error);
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User has been manually subscribed.",
      });
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error: any) => {
      console.error("Activate Subscription Mutation Error:", error);
      toast({
        title: "Error",
        description: error?.message
          ? `Failed to activate subscription: ${error.message}`
          : "Failed to activate subscription.",
        variant: "destructive",
      });
    },
  });

  const handleDeactivate = (subscriptionId: string) => {
    if (confirm("Are you sure you want to deactivate this user's subscription?")) {
      deactivateSubscription.mutate(subscriptionId);
    }
  };

  const handleActivate = (userId: string) => {
    if (confirm("Are you sure you want to manually activate a subscription for this user?")) {
      activateSubscription.mutate(userId);
    }
  };

  const handlePrevPage = () => setPage((p) => Math.max(0, p - 1));
  const handleNextPage = () => setPage((p) => p + 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Crown className="h-5 w-5" />
          User Management
          {users && (
            <span className="text-sm font-normal text-gray-500">
              ({users.length} users)
            </span>
          )}
        </CardTitle>

        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            className="max-w-md"
          />
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">Error loading users: {error.message}</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div>
            <UserTable
              users={users || []}
              onDeactivate={handleDeactivate}
              onActivate={handleActivate}
              activating={activateSubscription.isPending}
              deactivating={deactivateSubscription.isPending}
              searchTerm={searchTerm}
            />
            <div className="flex justify-center items-center mt-4 gap-4">
              <Button size="sm" variant="outline" onClick={handlePrevPage} disabled={page === 0}>
                Previous
              </Button>
              <span className="text-sm">Page {page + 1}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleNextPage}
                disabled={users && users.length < USERS_PER_PAGE}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminUserManagement;
